import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { SH } from 'common/components';
import InfoField from 'common/components/info-block/info-field/info-field';
import { DATE_SHORT_MMM_DD_YYYY, DATE_SHORT_MMM_D_YYYY_H_MMA } from 'common/constans/date.constants';
import { RentalAgencyChangeStatusEnum, rentalAgencyChangeStatusColorMap } from 'common/enums/rental-agency-change-status.enum';
import { RentalManagementTypeEnum } from 'common/enums/rental-management-type.enum';
import { rentalPaymentStatusColorMap, rentalPaymentStatusMap } from 'common/enums/rental-payment-status.enum';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatRentalAgencyChangeNumber, formatRentalFee, formatRentalPropertyAgencyLabel } from 'common/helpers/rental-property.helper';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import rentalAgencyChangeService from 'common/services/rental-agency-change-service/rental-agency-change.service';
import { IRentalAgencyChangeDetailsRead } from 'common/services/rental-agency-change-service/rental-agency-change.service.types';
import configurationStore from 'common/stores/configuration/configuration.store';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as ApproveIcon } from 'assets/icons/approve.svg';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import ApproveRentalAgencyChangeModal from '../approve-rental-agency-change-modal/approve-rental-agency-change-modal';
import RejectRentalAgencyChangeModal from '../reject-rental-agency-change-modal/reject-rental-agency-change-modal';
import styles from '../rental-portal.module.scss';

const approvalStatusTextMap: Record<RentalAgencyChangeStatusEnum, string> = {
  [RentalAgencyChangeStatusEnum.Pending]: 'Awaiting decision from property manager.',
  [RentalAgencyChangeStatusEnum.Approved]: 'Agent change approved.',
  [RentalAgencyChangeStatusEnum.Rejected]: 'Agent change rejected.',
};

export const RentalAgencyChangeDetails = observer(() => {
  const { agencyChangeId } = useParams<{ agencyChangeId: string }>();
  const [agencyChange, setAgencyChange] = useState<IRentalAgencyChangeDetailsRead>();

  const loadAgencyChange = useCallback(() => {
    const id = agencyChangeId ? parseInt(agencyChangeId, 10) : undefined;
    if (id !== undefined && !isNaN(id)) {
      rentalAgencyChangeService.getRentalAgencyChange(id).then(setAgencyChange);
    }
  }, [agencyChangeId]);

  useEffect(() => {
    loadAgencyChange();
  }, [loadAgencyChange]);

  const onReviewModalClose = (isSuccess?: boolean) => {
    if (isSuccess) {
      loadAgencyChange();
    }
  };

  const approveModal = useModal({ onClose: onReviewModalClose });
  const rejectModal = useModal({ onClose: onReviewModalClose });

  if (!agencyChange) {
    return null;
  }

  const isPending = agencyChange.status === RentalAgencyChangeStatusEnum.Pending;
  const statusLabel = isPending ? 'Pending Review' : agencyChange.status;
  const isRequestedAgencyManaged = agencyChange.requestedManagementType === RentalManagementTypeEnum.Agency;
  const rentalPortal = configurationStore.configuration?.rentalPortal;

  return (
    <div>
      <Card className={`eiq-card eiq-card-border ${styles.detailsCard}`} style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Space size={12}>
            <SH.HotkeyLink to="/eiq-manage/rental-portal/properties" className="go-back" label={<BackIcon />} />
            <div className={styles.headerTitleBlock}>
              <span className={styles.applicationNumber}>{formatRentalAgencyChangeNumber(agencyChange.id)}</span>
              <h2 className="main-title">{`${formatAddressLabel(agencyChange.address)} — Authorized Agent Change`}</h2>
              <span className={styles.submittedLine}>
                {`Submitted ${dayjs(agencyChange.createdAt).format(DATE_SHORT_MMM_DD_YYYY)} · ${agencyChange.communityName}`}
              </span>
            </div>
          </Space>
          <Space size={8}>
            <Tag className="eiq-tag" color={rentalAgencyChangeStatusColorMap[agencyChange.status]}>
              {statusLabel}
            </Tag>
            {isPending && (
              <>
                <Button
                  icon={<RejectIcon />}
                  size="middle"
                  type="primary"
                  className={styles.rejectButton}
                  onClick={rejectModal.showModal}
                  danger>
                  Reject
                </Button>
                <Button icon={<ApproveIcon />} size="middle" type="primary" onClick={approveModal.showModal}>
                  Approve
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Property</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Address" value={formatAddressLabel(agencyChange.address)} />
              <InfoField label="Community" value={agencyChange.communityName} />
              <InfoField label="County STR #" value={agencyChange.countyRegistrationNumber} />
              <InfoField
                label="Registration expiration"
                value={
                  agencyChange.registrationExpirationDate
                    ? dayjs(agencyChange.registrationExpirationDate).format(DATE_SHORT_MMM_DD_YYYY)
                    : '—'
                }
              />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Owner</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Name" value={agencyChange.ownerName} />
              <InfoField label="Email" value={agencyChange.ownerEmail ?? '—'} />
              <InfoField label="Phone" value={agencyChange.ownerPhone ?? '—'} />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Authorized agent</h3>
            <div className={styles.fieldsGrid}>
              <InfoField
                label="Current"
                value={formatRentalPropertyAgencyLabel(agencyChange.previousManagementType, agencyChange.previousManagingAgencyName)}
              />
              <InfoField
                label="Requested"
                value={formatRentalPropertyAgencyLabel(agencyChange.requestedManagementType, agencyChange.requestedManagingAgencyName)}
              />
              {isRequestedAgencyManaged && (
                <>
                  <InfoField label="Primary contact" value={agencyChange.requestedManagingAgencyContactName ?? '—'} />
                  <InfoField label="Email" value={agencyChange.requestedManagingAgencyEmail ?? '—'} />
                </>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Payment</h3>
            <div className={styles.fieldsColumn}>
              <InfoField
                label="Agent change fee"
                value={formatRentalFee(agencyChange.paymentStatus, rentalPortal?.isAgencyChangeFeeEnabled, rentalPortal?.agencyChangeFee)}
              />
              <InfoField
                label="Status"
                value={
                  <Tag className="eiq-tag" color={rentalPaymentStatusColorMap[agencyChange.paymentStatus]}>
                    {rentalPaymentStatusMap[agencyChange.paymentStatus]}
                  </Tag>
                }
              />
              {agencyChange.externalPaymentId && (
                <InfoField
                  label="Payment ID"
                  value={
                    <Typography.Text copyable ellipsis={{ tooltip: agencyChange.externalPaymentId }}>
                      {agencyChange.externalPaymentId}
                    </Typography.Text>
                  }
                />
              )}
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Approval status</h3>
            <div className={styles.fieldsColumn}>
              <span className={styles.approvalStatusText}>
                {`Last updated ${dayjs(agencyChange.updatedAt).format(DATE_SHORT_MMM_D_YYYY_H_MMA)}. ${
                  approvalStatusTextMap[agencyChange.status]
                }`}
              </span>
              {agencyChange.status === RentalAgencyChangeStatusEnum.Rejected && agencyChange.rejectionComment && (
                <InfoField
                  label="Rejection reason"
                  value={<span className={styles.rejectionComment}>{agencyChange.rejectionComment}</span>}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
      <RejectRentalAgencyChangeModal
        initData={{ agencyChangeId: agencyChange.id }}
        title="Reason for rejection"
        isOpen={rejectModal.isOpenModal}
        onClose={rejectModal.hideModal}
      />
      <ApproveRentalAgencyChangeModal
        initData={{ agencyChangeId: agencyChange.id }}
        title="Approve authorized agent change"
        isOpen={approveModal.isOpenModal}
        onClose={approveModal.hideModal}
      />
    </div>
  );
});
